// קומפוננטה ראשית שמנהלת את לוגיקת החיפוש, כולל debounce, קריאות API ותצוגת תוצאות בזמן אמת

import React, {useContext, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {FamilyContext} from '../../Context/FamilyHeadContext'
import {API_URL, doApiGet} from '../../services/apiService'
import SearchInput from './SearchInput'
import SearchResults from './SearchResults'

const UnifiedSearch = ({ variant = 'expandable' }) => {
    const [searchValue, setSearchValue] = useState('')
    const [results, setResults] = useState(null)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [debouncedValue, setDebouncedValue] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [countdownSeconds, setCountdownSeconds] = useState(null)
    const searchRef = useRef(null)
    const autoNavigateTimer = useRef(null)
    const countdownInterval = useRef(null)
    const {isMainSite, processPerson} = useContext(FamilyContext)
    const {pathPrefix} = useContext(FamilyContext)
    const {t, i18n} = useTranslation()
    const navigate = useNavigate()

    const isExpandable = variant === 'expandable'

    const handleClear = () => {
        setSearchValue('')
        setResults(null)
        setIsOpen(false)
        setCountdownSeconds(null)

        // נקה טיימרים אם קיימים
        if (autoNavigateTimer.current) {
            clearTimeout(autoNavigateTimer.current)
            autoNavigateTimer.current = null
        }

        if (countdownInterval.current) {
            clearInterval(countdownInterval.current)
            countdownInterval.current = null
        }
    }

    const handleSearch = () => {
        if (searchValue.trim()) {
            const encodedQuery = encodeURIComponent(searchValue)
            navigate(`${pathPrefix}results-page?query=${encodedQuery}`)
            setIsOpen(false)
        }
        handleClear()
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(searchValue)
        }, 300)

        return () => clearTimeout(timer)
    }, [searchValue])

    // בדיקת תוצאה יחידה ויצירת מעבר אוטומטי
    useEffect(() => {
        // נקה טיימרים קודמים
        if (autoNavigateTimer.current) {
            clearTimeout(autoNavigateTimer.current)
            autoNavigateTimer.current = null
        }

        if (countdownInterval.current) {
            clearInterval(countdownInterval.current)
            countdownInterval.current = null
        }

        setCountdownSeconds(null)

        // בדוק אם יש בדיוק תוצאה אחת
        if (results && results.length === 1 && isOpen) {
            // התחל ספירה לאחור מ-5
            setCountdownSeconds(5)

            // הפעל אינטרוול שיעדכן את הספירה לאחור כל שנייה
            countdownInterval.current = setInterval(() => {
                setCountdownSeconds(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownInterval.current)
                        countdownInterval.current = null
                        return 0
                    }
                    return prev - 1
                })
            }, 1000)

            // הפעל טיימר למעבר אוטומטי לאחר 5 שניות
            autoNavigateTimer.current = setTimeout(() => {
                const singleResult = results[0]
                navigate(singleResult.path)
                handleClear()
            }, 5000) // 5000 מילישניות = 5 שניות
        }

        // ניקוי כאשר הקומפוננטה מתפרקת
        return () => {
            if (autoNavigateTimer.current) {
                clearTimeout(autoNavigateTimer.current)
            }

            if (countdownInterval.current) {
                clearInterval(countdownInterval.current)
            }
        }
    }, [results, isOpen, navigate])

    useEffect(() => {
        const fetchResults = async () => {
            if (!debouncedValue.trim() || debouncedValue.trim().length < 2) {
                setResults(null)
                return
            }

            setLoading(true)
            setError('')
            try {
                const data = await doApiGet(
                    `${API_URL}/search?query=${encodeURIComponent(
                        debouncedValue,
                    )}&family=${isMainSite}&lang=${i18n.language}`,
                )
                const processedData = data.map((person) => processPerson(person))
                setResults(processedData)
            } catch (err) {
                console.error('Search error:', err)
                setError('שגיאה בתקשורת עם השרת')
            } finally {
                setLoading(false)
            }
        }

        fetchResults()
    }, [debouncedValue, isMainSite, i18n.language, processPerson])

    const resultsContainerStyles = {
        expandable:
            'absolute top-12 right-0 w-full max-w-lg bg-white rounded-xl shadow-lg border border-[#e6dfd4] overflow-hidden z-30',
        standalone:
            'mt-4 bg-white rounded-xl shadow-lg border border-[#e6dfd4] overflow-hidden',
    }

    return (
        <div
            ref={searchRef}
            className={`relative w-full ${!isExpandable ? 'max-w-2xl mx-auto' : ''}`}
        >
            <SearchInput
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                onSearch={handleSearch}
                onClear={handleClear}
                onFocus={() => setIsOpen(true)}
                variant={variant}
                placeholder={t('home.searchPlaceholder')}
            />

            {searchValue.trim() && isOpen && (
                <div className={resultsContainerStyles[variant]}>
                    <SearchResults
                        results={results}
                        loading={loading}
                        error={error}
                        onResultClick={() => {
                            setIsOpen(false)
                            handleClear()
                        }}
                        countdownSeconds={countdownSeconds}
                    />
                </div>
            )}
        </div>
    )
}

export default UnifiedSearch